import { Component, Element, Event, EventEmitter, Fragment, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import pfx from "../../utils/style-class-prefix-helper";
import { isMaxSize, isMobile } from "../../utils/screen-helper";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { KeyIds } from "../../constants/keys";
import { PAGINATION_LOCALIZATION } from "./constants/pagination-i18n";
import {
  PaginationLocalization,
  PaginationSchemeDefinition,
  PaginationSchemeName,
  PaginationTypes
} from "./constants/pagination-types";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { PaginationSchemeConfiguration } from "../../constants/colors";
import {
  PAGINATION_SCHEME_CONFIG,
  PAGINATION_SCHEME_MAP,
  PAGINATION_TYPE_DROPDOWN
} from "./constants/pagination-constants";

@Component({
  tag: "eon-ui-pagination",
  styleUrl: "pagination.scss",
  shadow: true
})
export class Pagination
  implements
    LocalizableComponent<PaginationLocalization>,
    ColorableComponent<PaginationSchemeConfiguration<PaginationSchemeDefinition>> {
  schemeConfig: PaginationSchemeConfiguration<PaginationSchemeDefinition> = PAGINATION_SCHEME_CONFIG;
  schemeMap = PAGINATION_SCHEME_MAP;

  localization: Localization<PaginationLocalization> = PAGINATION_LOCALIZATION;
  currentLocalization: PaginationLocalization;
  pageNumberInput: HTMLEonUiInputElement;

  @Element() hostElement: HTMLEonUiPaginationElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: PaginationSchemeName;

  /**
   * Select type of pagination
   */
  @Prop() paginationType: PaginationTypes = PAGINATION_TYPE_DROPDOWN;

  /**
   * Comma seperated list of page of page sizes
   */
  @Prop() pageSizes: string = "5,10,20,50,100";

  /**
   * Initial page size
   */
  @Prop() initialPageSize: number;

  /**
   * represents the current page index
   */
  @Prop({ mutable: true, reflect: true }) currentPage: number;

  /**
   * Total number of items
   */
  @Prop() totalItems: number;

  /**
   * The Items to be viewed per page
   */
  @Prop() pageSize: number = 5;

  /**
   * Begin
   */
  @State() begin: number;

  /**
   * End
   */
  @State() end: number;

  /**
   * Active scheme
   */
  @State() activeScheme: PaginationSchemeDefinition;

  /**
   * Whether it is a mobile device
   */
  @State() isMobile: boolean;

  /**
   * Whether it is a small mobile device
   */
  @State() isSmallMobile: boolean;

  /**
   * Fires when the page was increased
   */
  @Event() pageIncreased: EventEmitter<number>;

  /**
   * Fires when the page was decreased
   */
  @Event() pageDecreased: EventEmitter<number>;

  /**
   * Fires when the page was changed
   */
  @Event() pageChanged: EventEmitter<number>;

  /**
   * Fires when the page Size was changed
   */
  @Event() pageSizeChanged: EventEmitter<number>;

  @Listen("resize", { target: "window" })
  onViewportSizeChange() {
    this.isMobile = isMobile();
    this.isSmallMobile = isMaxSize(600);
  }

  componentWillLoad() {
    this.applyScheme();
    this.setCurrentLocalization();
    this.setBeginEnd();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("currentPage")
  @Watch("pageSize")
  setBeginEnd() {
    this.begin = (this.currentPage - 1) * this.pageSize;
    this.end = this.begin + this.pageSize > this.totalItems ? this.totalItems : this.begin + this.pageSize;
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  private get sortedPageSizeArray(): number[] {
    return this.pageSizes
      .split(",")
      .map((num) => parseInt(num))
      .sort((a, b) => {
        return a - b;
      });
  }

  private get numberOfPages(): number {
    return Math.ceil(Number(this.totalItems) / this.pageSize);
  }

  private setCurrentPage(page: number) {
    let currentPage = page;
    if (page > this.numberOfPages) {
      currentPage = this.numberOfPages;
    } else if (page < 1) {
      currentPage = 1;
    }
    this.currentPage = currentPage;
  }

  private get deactivatePageInput() {
    return !this.totalItems;
  }

  private get deactivateNextButton() {
    return this.currentPage === this.numberOfPages;
  }

  private get deactivatePreviousButton() {
    return this.currentPage === 1;
  }

  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.ENTER:
        const pageNumber =
          this.paginationType === "dropdown"
            ? Number(this.pageNumberInput.shadowRoot.querySelector("input").value)
            : parseInt((e.currentTarget as HTMLElement).innerHTML);

        this.setCurrentPage(pageNumber);
        this.pageChanged.emit(pageNumber);
        e.preventDefault();
        break;
    }
  }

  private setPageSize(pageSize: number) {
    if (pageSize) {
      this.pageSize = pageSize;
      this.setCurrentPage(1); // reset current page
      this.pageSizeChanged.emit(pageSize);
    }
  }

  private increasePage() {
    if (this.currentPage != this.numberOfPages) {
      this.pageIncreased.emit(this.currentPage + 1);
    }
  }

  private decreasePage() {
    if (this.currentPage > 1) {
      this.pageDecreased.emit(this.currentPage - 1);
    }
  }

  private renderPrevPageButton() {
    return (
      <eon-ui-button
        size="small"
        icon="chevron_small_left"
        class={pfx({ paging__current__button: true })}
        ariaLabel={this.currentLocalization.previous}
        onClick={() => this.decreasePage()}
        disabled={this.deactivatePreviousButton}
      ></eon-ui-button>
    );
  }

  private renderNextPageButton() {
    return (
      <eon-ui-button
        size="small"
        icon="chevron_small_right"
        class={pfx({ paging__current__button: true })}
        ariaLabel={this.currentLocalization.next}
        onClick={() => this.increasePage()}
        disabled={this.deactivateNextButton}
      ></eon-ui-button>
    );
  }

  createPaginationNumbers = () => {
    const paginationSize = isMobile() ? 5 : 7;
    const paginationMargin = isMobile() ? 2 : 3;

    const paginationElements = [];
    // List all the pages if number of pages smaller than pagination size
    if (this.numberOfPages <= paginationSize) {
      for (let i = 1; i <= this.numberOfPages; i++) {
        paginationElements.push(i);
      }
    } else if (this.currentPage <= paginationSize - paginationMargin) {
      // ... is only on the second-to-last place
      for (let i = 1; i <= paginationSize - 2; i++) {
        paginationElements.push(i);
      }
      paginationElements.push("...");
      paginationElements.push(this.numberOfPages);
    } else if (this.currentPage > this.numberOfPages - paginationSize + paginationMargin) {
      // ... is only on the second place
      paginationElements.push(1);
      paginationElements.push("...");
      for (let i = this.numberOfPages - paginationSize + 3; i <= this.numberOfPages; i++) {
        paginationElements.push(i);
      }
    } else {
      // ... is on the second and second-to-last place
      paginationElements.push(1);
      paginationElements.push("...");
      if (paginationSize === 7) {
        paginationElements.push(this.currentPage - 1);
      }
      paginationElements.push(this.currentPage);
      if (paginationSize === 7) {
        paginationElements.push(this.currentPage + 1);
      }
      paginationElements.push("...");
      paginationElements.push(this.numberOfPages);
    }

    return (
      <Fragment>
        <div class={pfx("pagination")}>
          {paginationElements.map((elm) => {
            return (
              <div
                class={pfx({
                  "pagination-element": true,
                  active: this.currentPage === elm,
                  disabled: elm === "..."
                })}
                onClick={() => {
                  this.setCurrentPage(elm);
                  this.pageChanged.emit(elm);
                }}
                onKeyDown={(e: KeyboardEvent) => this.handleKeyDown(e)}
                tabIndex={0}
                role="link"
              >
                {elm}
              </div>
            );
          })}
        </div>
      </Fragment>
    );
  };

  render() {
    return (
      <Host>
        <div class={pfx({ paging: true })}>
          <div class={pfx({ paging__column: true })}>
            <eon-ui-dropdown
              size="small"
              name="itemsPerPage"
              onValueChanged={(event) => this.setPageSize(Number(event.detail))}
              class={pfx({ paging__items__control: true })}
              value={`${this.initialPageSize}`}
              label={this.currentLocalization.itemsPerPage}
              labelBefore={true}
            >
              {this.sortedPageSizeArray.map((pageSize) => {
                if (Number(pageSize) <= this.totalItems) {
                  return <eon-ui-dropdown-option value={`${pageSize}`} label={`${pageSize}`}></eon-ui-dropdown-option>;
                }
              })}
            </eon-ui-dropdown>
          </div>
          <div
            class={pfx({
              paging__column: true,
              "paging__column--pagination-text": true
            })}
          >
            <eon-ui-text text-style={this.isMobile ? "copy-small" : "copy"} class={pfx({ paging__description: true })}>
              {this.begin === 0 ? "1" : this.begin + 1} - {this.end} {this.currentLocalization.of} {this.totalItems}{" "}
              {this.currentLocalization.items}
            </eon-ui-text>
          </div>
          {this.paginationType === "dropdown" && (
            <div>
              <div
                class={pfx({
                  paging__column: true,
                  "paging__column--mobile-100": true,
                  mobile: this.isSmallMobile
                })}
              >
                {!this.isSmallMobile && this.renderPrevPageButton()}
                <eon-ui-input
                  ref={(el) => (this.pageNumberInput = el)}
                  size="small"
                  class={pfx({ paging__current__input: true })}
                  name="itemsVisible"
                  type="number"
                  onKeyDown={(e: KeyboardEvent) => this.handleKeyDown(e)}
                  value={this.currentPage.toString()}
                  disabled={this.deactivatePageInput}
                  describedBy="paginationAddedInfo"
                  min="1"
                  max={this.numberOfPages.toString()}
                  label={this.currentLocalization.currentPage}
                  labelBefore={true}
                  width="55px"
                ></eon-ui-input>
                <eon-ui-text text-style="copy" id="paginationAddedInfo" class={pfx({ paging__current__label: true })}>
                  {this.currentLocalization.of} {this.numberOfPages}{" "}
                  <span class={pfx({ "sr-only": true })}>{this.currentLocalization.paginationAddedInfo}</span>
                </eon-ui-text>
                {!this.isSmallMobile && this.renderNextPageButton()}
              </div>
              {this.isSmallMobile && (
                <div class={pfx({ buttons: true })}>
                  {this.renderPrevPageButton()}
                  {this.renderNextPageButton()}
                </div>
              )}
            </div>
          )}
          {this.paginationType === "numbers" && (
            <div
              class={pfx({
                paging__column: true,
                "paging__column--pagination": true
              })}
            >
              <eon-ui-link
                icon="chevron_small_left"
                class={pfx({
                  "paging__current-button": true,
                  "pagination-element": true,
                  disabled: this.deactivatePreviousButton
                })}
                ariaLabel={this.currentLocalization.previous}
                onClick={() => this.decreasePage()}
                disabled={this.deactivatePreviousButton}
              ></eon-ui-link>
              {this.createPaginationNumbers()}
              <eon-ui-link
                icon="chevron_small_right"
                class={pfx({
                  "paging__current-button": true,
                  "pagination-element": true,
                  disabled: this.deactivateNextButton
                })}
                ariaLabel={this.currentLocalization.next}
                onClick={() => this.increasePage()}
                disabled={this.deactivateNextButton}
              ></eon-ui-link>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
